import { ref } from 'vue';
import { useExperienceEntity } from '@/entities/experience';
import { useJournalContent } from '@/entities/journalContent';
import { useSeoEntity } from '@/entities/seo';
import { getCities, type City } from '@/shared/api/experience';
import type { GeoItem } from '@/shared/models/journal';

export const useBootstrap = () => {
  const seoEntity = useSeoEntity();
  const experienceEntity = useExperienceEntity();
  const journalContent = useJournalContent();
  const journalContentEntity = useJournalContent();

  type SlugType = 'region' | 'country' | 'city';

  const cities = ref<GeoItem[]>([]);
  const countryObj = ref<{
    slug: string;
    name: string;
    name5: string;
    id: number;
    inObjPhrase: string;
  } | null>(null);

  const resetEntity = () => {
    journalContentEntity.reset();
    experienceEntity.reset();
    seoEntity.reset();
  };

  const getCitiesApi = async (slugType: SlugType, slug: string) => {
    const { data } = await getCities({
      query: { [`${slugType}__slug`]: slug, sorting: '-popular', page_size: 9999 },
    });

    if (!data) {
      return;
    }

    let countryFound: City | null = null;

    for (const el of data.results) {
      cities.value.push({
        nameRu: el.name_ru || '',
        slug: el.slug || '',
        id: el.id || 0,
      });

      if (!countryFound && el.country?.slug) {
        countryFound = el.country;
      }
    }

    if (countryFound) {
      countryObj.value = {
        slug: countryFound.slug || '',
        name: countryFound.name_ru || '',
        name5: countryFound.name_5_ru || '',
        id: countryFound.id || 0,
        inObjPhrase: countryFound.in_obj_phrase || '',
      };
    } else {
      countryObj.value = null;
    }

    return {
      cities: cities.value,
      countryObj: countryObj.value,
    };
  };

  const fetchData = async (slugType: SlugType, slug: string, pageSize: number) => {
    const result = await Promise.allSettled([
      seoEntity.loadSeo(slug, slugType),
      getCitiesApi(slugType, slug),
      journalContent.getArticles({ slugType, slug, pageSize }),
      experienceEntity.load({
        [`${slugType}Slug`]: slug,
        type: 'private,group',
        limit: 6,
        priceFormat: 'travelers',
        extendedMovementTypes: true,
        photosCount: 1,
      }),
    ]);

    return {
      cities: result[1].status === 'fulfilled' ? result[1].value : null,
    };
  };

  const fetchSitghts = async (slugType: SlugType, slug: string) => {
    const result = await Promise.allSettled([
      seoEntity.loadSeo(slug, slugType),
      getCitiesApi(slugType, slug),
      experienceEntity.load({
        [`${slugType}Slug`]: slug,
        type: 'private,group',
        limit: 6,
        priceFormat: 'travelers',
        extendedMovementTypes: true,
        photosCount: 1,
      }),
    ]);

    return {
      cities: result[1].status === 'fulfilled' ? result[1].value : null,
    };
  };

  return {
    fetchData,
    resetEntity,
    fetchSitghts,
  };
};
